import React, { useState, useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Table, Button, Badge, Input } from "reactstrap"
import { FaEdit, FaPlus } from "react-icons/fa"
import OpportunityModal from "./OpportunityModal"

const OpportunitiesIndex = () => {
  document.title = "Opportunities | Vintage Infra Equipment Sol PVT LTD"
  const [opportunities, setOpportunities] = useState([
    {
      id: 1,
      customer: "ABC Infra Pvt Ltd",
      equipment: "Excavator",
      model: "CAT 320D",
      value: 4500000,
      stage: "Negotiation",
      probability: 70,
      closeDate: "2026-02-20",
    },
  ])

  const [search, setSearch] = useState("")
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const initialForm = {
    id: null,
    customer: "",
    equipment: "",
    model: "",
    value: "",
    stage: "Qualification",
    probability: "",
    closeDate: "",
  }

  const [formData, setFormData] = useState(initialForm)

  const toggleModal = () => setModalOpen(!modalOpen)

  const openAdd = () => {
    setIsEdit(false)
    setFormData(initialForm)
    toggleModal()
  }

  const openEdit = data => {
    setIsEdit(true)
    setFormData(data)
    toggleModal()
  }

  const handleSave = () => {
    if (isEdit) {
      setOpportunities(prev =>
        prev.map(o => (o.id === formData.id ? formData : o))
      )
    } else {
      setOpportunities(prev => [...prev, { ...formData, id: Date.now() }])
    }
    toggleModal()
  }

  // 🔍 Search logic
  const filteredData = useMemo(() => {
    return opportunities.filter(o =>
      `${o.customer} ${o.equipment} ${o.model}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )
  }, [search, opportunities])

  // 📄 Pagination
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(start, start + pageSize)

  const stageColor = stage => {
    switch (stage) {
      case "Won":
        return "success"
      case "Lost":
        return "danger"
      case "Negotiation":
        return "warning"
      default:
        return "secondary"
    }
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Sales" breadcrumbItem="Opportunities" />

      <Card>
        <CardBody>
          {/* Top Controls */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-2">
              <Input
                type="select"
                value={pageSize}
                className="rounded-4"
                style={{ width: 90 }}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                placeholder="Search..."
                className="rounded-4"
                style={{ width: 250 }}
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>

            <Button className="btn-blue rounded-4" onClick={openAdd}>
              <FaPlus /> Add Opportunity
            </Button>
          </div>

          {/* Table */}
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Equipment</th>
                <th>Model</th>
                <th>Value (₹)</th>
                <th>Stage</th>
                <th>Probability</th>
                <th>Close Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(item => (
                <tr key={item.id}>
                  <td>{item.customer}</td>
                  <td>{item.equipment}</td>
                  <td>{item.model}</td>
                  <td>{item.value}</td>
                  <td>
                    <Badge color={stageColor(item.stage)} className="p-2 fs-6">{item.stage}</Badge>
                  </td>
                  <td>{item.probability}%</td>
                  <td>{item.closeDate}</td>
                  <td>
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => openEdit(item)}
                    >
                      <FaEdit />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination Footer */}
          <nav className="d-flex justify-content-between mt-3">
            <h6>
              Showing {start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h6>

            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 && "disabled"}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages && "disabled"
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(p => Math.min(p + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      {/* Modal */}
      <OpportunityModal
        isOpen={modalOpen}
        toggle={toggleModal}
        isEdit={isEdit}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
      />
    </div>
  )
}

export default OpportunitiesIndex
